import { useState, useEffect } from "react";
import {
  Check,
  ChevronsUpDown,
  MapPin,
  ExternalLink,
  Lock,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { COUNTRIES, normalizeCountryCode } from "@/lib/data/countries";
import { HONDURAS_DEPARTMENTS, getDepartmentByCity } from "@/lib/data/honduras";
import { US_STATES } from "@/lib/data/us-states";
import { useCommonI18n } from "@/hooks/useModuleI18n";

interface LocationFieldProps {
  defaultCountry?: string;
  defaultCity?: string;
  defaultAddress?: string;
  defaultState?: string;
  defaultZipCode?: string;
  defaultMapUrl?: string;
  countryName?: string;
  cityName?: string;
  addressName?: string;
  stateName?: string;
  zipCodeName?: string;
  mapUrlName?: string;
  disabled?: boolean;
  required?: boolean;
  requiredForHonduras?: boolean;
  requiredForUSA?: boolean;
  showAddress?: boolean;
  showZipCode?: boolean;
  showMapUrl?: boolean;
  className?: string;
}

export function LocationField({
  defaultCountry,
  defaultCity = "",
  defaultAddress = "",
  defaultState = "",
  defaultZipCode = "",
  defaultMapUrl = "",
  countryName = "country",
  cityName = "city",
  addressName = "address",
  stateName = "state",
  zipCodeName = "zipCode",
  mapUrlName = "mapUrl",
  disabled = false,
  required = false,
  requiredForHonduras = true,
  requiredForUSA = false,
  showAddress = true,
  showZipCode = false,
  showMapUrl = false,
  className,
}: LocationFieldProps) {
  const { t } = useCommonI18n();

  const [open, setOpen] = useState(false);
  const [country, setCountry] = useState(() =>
    normalizeCountryCode(defaultCountry),
  );

  const isHonduras = country === "HN";
  const isUSA = country === "US";

  const isLocationRequired =
    required ||
    (isHonduras && requiredForHonduras) ||
    (isUSA && requiredForUSA);

  const [department, setDepartment] = useState(() => {
    if (isHonduras && defaultCity) {
      return getDepartmentByCity(defaultCity) ?? "";
    }
    return "";
  });

  const [usState, setUsState] = useState(defaultState);
  const [city, setCity] = useState(defaultCity);
  const [zipCode, setZipCode] = useState(defaultZipCode);
  const [address, setAddress] = useState(defaultAddress);
  const [mapUrl, setMapUrl] = useState(defaultMapUrl);

  // Sync state with default props when they change (e.g., after async data loads)
  useEffect(() => {
    const normalizedCountry = normalizeCountryCode(defaultCountry);
    setCountry(normalizedCountry);

    // Reset department for Honduras
    if (normalizedCountry === "HN" && defaultCity) {
      setDepartment(getDepartmentByCity(defaultCity) ?? "");
    } else {
      setDepartment("");
    }
  }, [defaultCountry, defaultCity]);

  useEffect(() => {
    setCity(defaultCity);
  }, [defaultCity]);

  useEffect(() => {
    setAddress(defaultAddress);
  }, [defaultAddress]);

  useEffect(() => {
    setUsState(defaultState);
  }, [defaultState]);

  useEffect(() => {
    setZipCode(defaultZipCode);
  }, [defaultZipCode]);

  useEffect(() => {
    setMapUrl(defaultMapUrl);
  }, [defaultMapUrl]);

  const selectedCountry = COUNTRIES.find((c) => c.value === country);
  const currentDept = HONDURAS_DEPARTMENTS.find((d) => d.value === department);

  const handleCountryChange = (value: string) => {
    setCountry(value);
    setDepartment("");
    setUsState("");
    setCity("");
    setOpen(false);
  };

  const handleDepartmentChange = (value: string) => {
    setDepartment(value);
    setCity("");
  };

  return (
    <div className={cn("space-y-4", className)}>
      {/* Country */}
      <div className="space-y-2">
        <Label className="flex items-center gap-1.5 text-xs font-medium">
          <MapPin className="w-3.5 h-3.5" />
          {t("location.country")}
          {isLocationRequired && <Lock className="h-3 w-3 text-primary" />}
        </Label>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              role="combobox"
              aria-expanded={open}
              disabled={disabled}
              className={cn(
                "w-full justify-between font-normal h-10 md:h-9 border-ring/30 bg-background dark:bg-input/30",
                "shadow-[0_2px_6px_rgba(0,0,0,0.06)] hover:shadow-[0_2px_8px_rgba(0,0,0,0.08)]",
                "dark:shadow-[0_2px_6px_rgba(0,0,0,0.1)] dark:hover:shadow-[0_3px_10px_rgba(0,0,0,0.12)]",
                "opacity-70 hover:opacity-100 focus-visible:opacity-100 transition-all",
              )}
            >
              {selectedCountry ? (
                <span className="flex items-center gap-2">
                  <span>{selectedCountry.flag}</span>
                  <span>{selectedCountry.label}</span>
                </span>
              ) : (
                <span className="text-muted-foreground">
                  {t("location.selectCountry")}
                </span>
              )}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-[var(--radix-popover-trigger-width)] p-0"
            align="start"
          >
            <Command>
              <CommandInput placeholder={t("location.searchCountry")} />
              <CommandList>
                <CommandEmpty>{t("location.countryNotFound")}</CommandEmpty>
                <CommandGroup>
                  {COUNTRIES.map((c) => (
                    <CommandItem
                      key={c.value}
                      value={c.label}
                      onSelect={() => handleCountryChange(c.value)}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          country === c.value ? "opacity-100" : "opacity-0",
                        )}
                      />
                      <span className="mr-2">{c.flag}</span>
                      {c.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <input type="hidden" name={countryName} value={country} />
      </div>

      {/* Honduras: Department + Municipality */}
      {isHonduras && (
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label className="text-xs font-medium flex items-center gap-1.5 text-foreground/90">
              {t("location.department")}
              {isLocationRequired && <Lock className="h-3 w-3 text-primary" />}
            </Label>
            <Select
              value={department}
              onValueChange={handleDepartmentChange}
              disabled={disabled}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t("location.selectDepartment")} />
              </SelectTrigger>
              <SelectContent>
                {HONDURAS_DEPARTMENTS.map((dept) => (
                  <SelectItem key={dept.value} value={dept.value}>
                    {dept.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-medium flex items-center gap-1.5 text-foreground/90">
              {t("location.municipality")}
              {isLocationRequired && <Lock className="h-3 w-3 text-primary" />}
            </Label>
            <Select
              value={city}
              onValueChange={setCity}
              disabled={disabled || !department}
            >
              <SelectTrigger className="w-full">
                <SelectValue
                  placeholder={
                    department
                      ? t("location.selectMunicipality")
                      : t("location.selectDepartmentFirst")
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {currentDept?.municipios.map((m) => (
                  <SelectItem key={m.value} value={m.value}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {showZipCode && (
            <div className="space-y-2">
              <Label className="text-xs font-medium text-foreground/90">
                {t("location.zipCode")}
              </Label>
              <Input
                name={zipCodeName}
                placeholder={t("location.zipCodePlaceholder")}
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value)}
                disabled={disabled}
                maxLength={10}
              />
            </div>
          )}
          {/* Hidden input for state (department) in Honduras */}
          <input type="hidden" name={stateName} value={department} />
        </div>
      )}

      {/* USA: State + City */}
      {isUSA && (
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label className="text-xs font-medium flex items-center gap-1.5 text-foreground/90">
              {t("location.state")}
              {isLocationRequired && <Lock className="h-3 w-3 text-primary" />}
            </Label>
            <Select
              value={usState}
              onValueChange={setUsState}
              disabled={disabled}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t("location.selectState")} />
              </SelectTrigger>
              <SelectContent>
                {US_STATES.map((state) => (
                  <SelectItem key={state.value} value={state.value}>
                    {state.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input type="hidden" name={stateName} value={usState} />
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-medium flex items-center gap-1.5 text-foreground/90">
              {t("location.city")}
              {isLocationRequired && <Lock className="h-3 w-3 text-primary" />}
            </Label>
            <Input
              placeholder={t("location.cityPlaceholder")}
              value={city}
              onChange={(e) => setCity(e.target.value)}
              disabled={disabled}
              required={isLocationRequired}
            />
          </div>

          {showZipCode && (
            <div className="space-y-2">
              <Label className="text-xs font-medium text-foreground/90">
                {t("location.zipCodeUS")}
              </Label>
              <Input
                name={zipCodeName}
                placeholder={t("location.zipCodeUSPlaceholder")}
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value)}
                disabled={disabled}
                maxLength={10}
              />
            </div>
          )}
        </div>
      )}

      {/* Other countries: free-text city */}
      {!isHonduras && !isUSA && (
        <div className="space-y-2">
          <Label className="text-xs font-medium flex items-center gap-1.5 text-foreground/90">
            {t("location.city")}
            {isLocationRequired && <Lock className="h-3 w-3 text-primary" />}
          </Label>
          <Input
            placeholder={t("location.cityPlaceholder")}
            value={city}
            onChange={(e) => setCity(e.target.value)}
            disabled={disabled}
            required={isLocationRequired}
          />
        </div>
      )}
      <input type="hidden" name={cityName} value={city} />

      {/* Address */}
      {showAddress && (
        <div className="space-y-2">
          <Label className="text-xs font-medium flex items-center gap-1.5 text-foreground/90">
            {t("location.address")}
            {isLocationRequired && <Lock className="h-3 w-3 text-primary" />}
          </Label>
          <Textarea
            name={addressName}
            placeholder={t("location.addressPlaceholder")}
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            disabled={disabled}
            required={isLocationRequired}
            rows={2}
          />
        </div>
      )}

      {/* Map URL - useful for Honduras deliveries */}
      {showMapUrl && (
        <div className="space-y-2">
          <Label className="text-xs font-medium text-foreground/90">{t("location.mapUrl")}</Label>
          <div className="flex gap-2">
            <Input
              name={mapUrlName}
              type="url"
              placeholder={t("location.mapUrlPlaceholder")}
              value={mapUrl}
              onChange={(e) => setMapUrl(e.target.value)}
              disabled={disabled}
              className="flex-1"
            />
            {mapUrl && (
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => window.open(mapUrl, "_blank")}
                title={t("location.openInMaps")}
              >
                <ExternalLink className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
